import useReaderStore from '../stores/reader';
import useNavigationControls from './use-navigation-controls';
import { storeToRefs } from 'pinia';
import useMediaStore from '../stores/media';
import useAudioTracks from './use-audio-tracks';
import useRenditionStore from '../stores/rendition';
import useNavigationStore from '../stores/navigation';
import useNavigationTriggerStore from '../stores/navigation-trigger';
import { NavigationDirection, Track } from '../types';

const useBackgroundPlaybackNavigation = () => {
  const { isContinuous } = storeToRefs(useReaderStore());
  const mediaStore = useMediaStore();
  const { currentTrack, currentPageTracks } = storeToRefs(mediaStore);
  const { setIsLastTrack, setCurrentTrack, setIsUserChangingTrack } = mediaStore;
  const { isRTL, isReflowable } = storeToRefs(useRenditionStore());
  const { totalSpineItems } = storeToRefs(useNavigationStore());
  const { navigateForward, navigateBack } = useNavigationControls();
  const { setNavigationTrigger } = useNavigationTriggerStore();
  const {
    loadNextSpineTracksByHrefAndPlay,
    loadPrevSpineTracksByHrefAndPlay,
    loadNeighborSpineTracks,
    playTracksFromList,
  } = useAudioTracks();

  const goToNextForBackgroundPlayback = async () => {
    setNavigationTrigger('LOCK_SCREEN_NEXT');
    setIsUserChangingTrack(true);
    if (isContinuous.value) {
      navigateForward();
      return;
    }

    if (!currentTrack.value) return;

    //TODO: Add better way to navigate reflowable with only one spine, e.g. some sort of actual page navigation.
    if (isReflowable.value && totalSpineItems.value === 1) {
      const direction = isRTL.value ? 'prev' : 'next';
      await jumpToTrackByOffset(5, direction, false);
      return;
    }

    const currentHref = currentTrack.value.text.src.split('#')[0];
    if (isRTL.value) {
      await loadPrevSpineTracksByHrefAndPlay(currentHref);
    } else {
      await loadNextSpineTracksByHrefAndPlay(currentHref);
    }
  };

  const goToPreviousForBackgroundPlayback = async () => {
    setNavigationTrigger('LOCK_SCREEN_PREVIOUS');
    setIsLastTrack(false);
    setIsUserChangingTrack(true);
    if (isContinuous.value) {
      navigateBack();
      return;
    }

    if (!currentTrack.value) return;

    //TODO: Add better way to navigate books with only one spine, e.g. some sort of actual page navigation.
    if (totalSpineItems.value === 1) {
      const direction = isRTL.value ? 'next' : 'prev';
      await jumpToTrackByOffset(5, direction, false);
      return;
    }

    const currentHref = currentTrack.value.text.src.split('#')[0];
    if (isRTL.value) {
      await loadNextSpineTracksByHrefAndPlay(currentHref);
    } else {
      await loadPrevSpineTracksByHrefAndPlay(currentHref);
    }
  };

  /**
   * Jumps through audio tracks by a given offset and handles spine boundaries.
   * @param offset - Number of tracks to move (positive integer).
   * @param direction - 'next' to move forward in time, 'prev' to move backward.
   * @param crossSpineNavigation - If true, allows loading neighboring spine tracks if target track is out of bounds.
   * @return - Returns true if the track jump succeeded, otherwise false.
   */
  const jumpToTrackByOffset = async (offset: number, direction: NavigationDirection, crossSpineNavigation: boolean) => {
    if (!currentTrack.value) return false;

    const currentTrackIndex = findTrackIndex(currentTrack.value, currentPageTracks.value);

    if (currentTrackIndex === -1) return false;

    //Calculates time-based target (next = forward in time, prev = backward in time)
    const targetTrackIndex = direction === 'next' ? currentTrackIndex + offset : currentTrackIndex - offset;

    const isTrackInBounds = trackInBounds(targetTrackIndex, currentPageTracks.value.length);
    //Case 1: Target track exists in current page tracks
    if (isTrackInBounds) {
      const newTrack = currentPageTracks.value[targetTrackIndex];
      setIsUserChangingTrack(true);
      setCurrentTrack(newTrack);
      return true;
    }

    // Prevent cross spine navigation e.g., for single-spine reflowable books
    if (!crossSpineNavigation) return false;

    //Case 2: Needs to load neighbor spine tracks.
    const currentPageTracksLength = currentPageTracks.value.length; //Store this value as loadNeighborSpineTracks will change it.

    // Map playback intent to physical spine direction (In RTL, 'next' chapter is the 'prev' spine file)
    const nextSpineDirection = getNextSpineDirection(direction);
    const currentHref = currentTrack.value.text.src.split('#')[0];
    const loadTracksResults = await loadNeighborSpineTracks(currentHref, nextSpineDirection);

    if (!loadTracksResults) return false;

    // Calculate landing index in the neighbor spine:
    // Forward: Subtract previous tracks length from the total overflow
    // Backward: Add the negative overflow to the end of the new tracks length
    const offsetIntoNewSpine =
      direction == 'next'
        ? targetTrackIndex - currentPageTracksLength
        : loadTracksResults.tracks.length + targetTrackIndex;

    const isTrackWithinNewBounds = trackInBounds(offsetIntoNewSpine, loadTracksResults.tracks.length);
    if (!isTrackWithinNewBounds) {
      //TODO: Add support for continuing loading neighbor spine tracks while we still are within bounds.
      console.warn('Offset exceeds available tracks in neighbor spine');
      return false;
    }

    setIsUserChangingTrack(true);
    await playTracksFromList(loadTracksResults.tracks, offsetIntoNewSpine);
    return true;
  };

  /**
   * Returns the index of a track within a track list.
   * @param targetTrack - The track object to find.
   * @param tracks - The array of tracks to search within.
   */
  const findTrackIndex = (targetTrack: Track, tracks: Track[]) => {
    return tracks.findIndex(
      (track) => track.text.src === targetTrack.text.src && track.audio.src === targetTrack.audio.src,
    );
  };

  /**
   * Validates if a track index exists within the bounds of a list.
   * @param targetTrackIndex - The index to check.
   * @param tracksListLength - Total length of the track array.
   */
  const trackInBounds = (targetTrackIndex: number, tracksListLength: number): boolean => {
    return targetTrackIndex >= 0 && targetTrackIndex < tracksListLength;
  };

  /**
   * Maps logical playback intent to physical EPUB spine direction, accounting for whether the book is
   * RTL (Right-to-Left) or not.
   * @param navigationDirection - The intended movement ('next' or 'prev').
   */
  const getNextSpineDirection = (navigationDirection: NavigationDirection): NavigationDirection => {
    if (navigationDirection === 'next') {
      return isRTL.value ? 'prev' : 'next';
    }
    return isRTL.value ? 'next' : 'prev';
  };

  return {
    goToNextForBackgroundPlayback,
    goToPreviousForBackgroundPlayback,
    jumpToTrackByOffset,
  };
};

export default useBackgroundPlaybackNavigation;
